import {useState, useCallback, useMemo} from 'react';
import {useAuth} from '../../contexts/auth.provider';
import {PersonalInfoFormData} from './personal-info-form';
import {CompanyDetailsFormData} from './company-details-form';
import {
  AccountServiceImpl,
  JobTitleServiceImpl,
  WorkExperienceServiceImpl,
  OrganizationServiceImpl,
  JobTitleCreateParams,
  WorkExperienceCreateParams,
} from '../../services';
import {WorkExperienceType} from '../../models/work-experience.model';
import {AccountUpdatePayload, PhoneNumber, ResourceSource} from '../../models';
import {toast} from 'react-toastify';
import {createEmploymentTenureRRule, parseEmploymentTenure} from '../../utils';

enum FileUploadType {
  LOGO = 'logo',
  COVER = 'cover',
}

export const useSettingsData = () => {
  const {user, account, organizationInfo, updateAccount: updateAccountInContext} = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accountService = useMemo(() => new AccountServiceImpl(), []);
  const jobTitleService = useMemo(() => new JobTitleServiceImpl(), []);
  const workExperienceService = useMemo(() => new WorkExperienceServiceImpl(), []);
  const organizationService = useMemo(() => new OrganizationServiceImpl(), []);

  const processWorkExperiences = useCallback((workExperiencesData: any[]) => {
    return workExperiencesData
      .map(we => {
        const {startDate} = parseEmploymentTenure(we.employmentTenure);
        return {
          ...we,
          parsedStartDate: startDate,
        };
      })
      .filter(we => we.parsedStartDate)
      .sort((a, b) => {
        return (b.parsedStartDate?.getTime() || 0) - (a.parsedStartDate?.getTime() || 0);
      });
  }, []);

  const fetchLatestWorkExperience = useCallback(
    async (userId: string, organizationId: string) => {
      try {
        const workExperiencesResponse = await workExperienceService.getAllByAccountOrganization(userId, organizationId);

        if (workExperiencesResponse.data && workExperiencesResponse.data.length > 0) {
          const workExperiences = processWorkExperiences(workExperiencesResponse.data);

          if (workExperiences.length > 0) {
            return workExperiences[0];
          }
        }
      } catch (workExperienceError) {
        console.warn('Failed to fetch work experiences for initial values:', workExperienceError);
      }
      return null;
    },
    [workExperienceService, processWorkExperiences],
  );

  const getPersonalInfoInitialValues = useCallback(async (): Promise<PersonalInfoFormData> => {
    let jobTitle = account?.title || '';
    let location = '';

    if (user?.id && organizationInfo?.organization?.id) {
      const latestWorkExperience = await fetchLatestWorkExperience(user.id, organizationInfo.organization.id);

      if (latestWorkExperience) {
        jobTitle = latestWorkExperience.jobTitle?.name || jobTitle;
        location = latestWorkExperience.location || location;
      }
    }

    return {
      firstName: account?.firstName || user?.firstName || '',
      lastName: account?.lastName || user?.lastName || '',
      email: account?.email || user?.email || '',
      phoneNumber: account?.phoneNumber || new PhoneNumber({countryCode: '', phoneNumber: ''}),
      jobTitle,
      location,
      bio: account?.bio || '',
    };
  }, [user, account, organizationInfo, fetchLatestWorkExperience]);

  const createEmptyCompanyDetails = useCallback((): CompanyDetailsFormData => {
    return {
      companyName: '',
      companySize: '',
      industry: '',
      website: '',
      description: '',
      logoImg: '',
      coverImage: '',
      logoFile: undefined,
      coverImageFile: undefined,
    };
  }, []);

  const mapOrganizationToFormData = useCallback((org: any): CompanyDetailsFormData => {
    return {
      companyName: org?.name || '',
      companySize: org?.companySize || '',
      industry: org?.industry || '',
      website: org?.companyUrl || '',
      description: org?.description || '',
      logoImg: org?.getLogoUrl ? org.getLogoUrl() : '',
      coverImage: org?.getCoverImageUrl ? org.getCoverImageUrl() : '',
      logoFile: undefined,
      coverImageFile: undefined,
    };
  }, []);

  const getCompanyDetailsInitialValues = useCallback(async (): Promise<CompanyDetailsFormData> => {
    const organization = organizationInfo?.organization;

    if (!organization?.id) {
      return createEmptyCompanyDetails();
    }

    try {
      const freshOrganization = await organizationService.getOrganizationById(organization.id);
      return mapOrganizationToFormData(freshOrganization);
    } catch (organizationError) {
      console.warn('Failed to fetch fresh organization data, using cached data:', organizationError);
      return mapOrganizationToFormData(organization);
    }
  }, [organizationInfo, organizationService, createEmptyCompanyDetails, mapOrganizationToFormData]);

  const savePersonalInfo = useCallback(
    async (data: PersonalInfoFormData): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        if (!user?.id || !organizationInfo?.organization?.id) {
          throw new Error('User ID or Organization ID not found');
        }

        const currentAccountData = await getPersonalInfoInitialValues();

        const phoneNumberChanged = JSON.stringify(data.phoneNumber) !== JSON.stringify(currentAccountData.phoneNumber);
        const emailChanged = data.email !== currentAccountData.email;

        const accountUpdatePayload: AccountUpdatePayload = {
          firstName: data.firstName,
          lastName: data.lastName,
          bio: data.bio,
          ...(phoneNumberChanged && {phoneNumber: data.phoneNumber}),
          ...(emailChanged && {email: data.email}),
        };

        const accountResponse = await accountService.updateAccount(user.id, accountUpdatePayload);
        if (accountResponse.error) {
          throw new Error(accountResponse.error);
        }

        if (accountResponse.data) {
          updateAccountInContext(accountResponse.data);
        }

        const jobTitleParams = new JobTitleCreateParams(user.id, data.jobTitle);
        const jobTitleResponse = await jobTitleService.createJobTitle(jobTitleParams);
        if (jobTitleResponse.error) {
          throw new Error(jobTitleResponse.error);
        }

        if (jobTitleResponse.data && organizationInfo.accountOrganization.organization.createdAt) {
          const orgCreatedDate = new Date(organizationInfo.accountOrganization.organization.createdAt);
          const employmentTenure = createEmploymentTenureRRule(orgCreatedDate);

          const workExperienceParams = new WorkExperienceCreateParams({
            accountId: user.id,
            organizationId: organizationInfo.organization.id,
            employmentTenure,
            isCurrentlyWorking: true,
            jobTitleId: jobTitleResponse.data.id,
            jobDescription: '',
            type: WorkExperienceType.EXPERIENCE,
            location: data.location || undefined,
          });

          const workExperienceResponse = await workExperienceService.createWorkExperience(workExperienceParams);
          if (workExperienceResponse.error) {
            throw new Error(workExperienceResponse.error);
          }
        }

        toast.success('Personal information saved successfully!');
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to save personal information';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user, organizationInfo, accountService, jobTitleService, workExperienceService, updateAccountInContext, getPersonalInfoInitialValues],
  );

  const uploadFile = useCallback(
    async (file: File, fileName: string, userId: string, toastId: any, fileType: FileUploadType): Promise<string | undefined> => {
      const formData = new FormData();
      formData.append('source', ResourceSource.CRED);
      formData.append('file', file, fileName || file.name);

      const uploadMessage = fileType === FileUploadType.LOGO ? 'Uploading company logo...' : 'Uploading cover image...';
      const successMessage =
        fileType === FileUploadType.LOGO ? 'Company logo uploaded successfully!' : 'Cover image uploaded successfully!';
      const errorMessage = fileType === FileUploadType.LOGO ? 'Failed to upload company logo' : 'Failed to upload cover image';

      toast.update(toastId, {render: uploadMessage, isLoading: true});

      const {data: uploadData, error: uploadErr} = await accountService.createReccoResource(userId, formData);

      if (uploadData && !uploadErr) {
        toast.update(toastId, {render: successMessage, isLoading: true});
        return uploadData[0]?.id;
      } else {
        setError(errorMessage);
        toast.update(toastId, {render: `${errorMessage}.`, type: 'error', isLoading: false, autoClose: 5000});
        throw new Error(errorMessage);
      }
    },
    [accountService],
  );

  const saveCompanyDetails = useCallback(
    async (data: CompanyDetailsFormData): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        if (!user?.id || !organizationInfo?.organization?.id) {
          throw new Error('User ID or Organization ID not found');
        }

        let logoResourceId: string | undefined;
        let coverImageResourceId: string | undefined;
        const toastId = toast.loading('Saving company details...');

        if (data.logoFile) {
          logoResourceId = await uploadFile(data.logoFile, data.logoImg || '', user.id, toastId, FileUploadType.LOGO);
        }

        if (data.coverImageFile) {
          coverImageResourceId = await uploadFile(data.coverImageFile, data.coverImage || '', user.id, toastId, FileUploadType.COVER);
        }

        toast.update(toastId, {render: 'Finalizing company details...', isLoading: true});

        const organizationUpdates: Partial<any> = {
          name: data.companyName,
          companySize: data.companySize,
          industry: data.industry,
          companyUrl: data.website,
          description: data.description,
          ...(logoResourceId && {logoImg: logoResourceId}),
          ...(coverImageResourceId && {coverImage: coverImageResourceId}),
        };

        const orgResponse = await organizationService.updateOrganization(organizationInfo.organization.id, user.id, organizationUpdates);

        if (!orgResponse) {
          throw new Error('Failed to update organization');
        }

        toast.update(toastId, {render: 'Company details saved successfully!', type: 'success', isLoading: false, autoClose: 5000});
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to save company details';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user, organizationInfo, accountService, organizationService, uploadFile],
  );

  return {
    loading,
    error,
    getPersonalInfoInitialValues,
    getCompanyDetailsInitialValues,
    savePersonalInfo,
    saveCompanyDetails,
  };
};
